import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, filter, catchError, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Permissao, PermissaoDTO, UsuarioPermissaoDTO, AtribuirPermissaoRequest } from '../models/permissao.model';
import { AuthService } from './auth.service';
import { AvaliadorService } from './avaliador.service';

@Injectable({
  providedIn: 'root'
})
export class PermissaoService {
  private apiUrl = `${environment.apiUrl}/admin/permissoes`;

  // Cache de permissões por edital (para evitar múltiplas requisições)
  private permissoesCache: Map<string, PermissaoDTO[]> = new Map();
  private cacheTimestamps: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

  // Cache de permissões globais no localStorage
  private readonly GLOBAL_PERMISSIONS_KEY = 'global_permissions';
  private readonly GLOBAL_PERMISSIONS_TIMESTAMP_KEY = 'global_permissions_timestamp';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private avaliadorService: AvaliadorService
  ) { }

  /**
   * Lista todas as permissões disponíveis (catálogo)
   * GET /api/admin/permissoes/catalogo
   */
  listarCatalogo(vinculoEdital: boolean | null = null): Observable<PermissaoDTO[]> {
    let params = new HttpParams();
    if (vinculoEdital !== null) {
      params = params.set('vinculoEdital', vinculoEdital.toString());
    }
    return this.http.get<PermissaoDTO[]>(`${this.apiUrl}/catalogo`, { params });
  }

  /**
   * Lista permissões com status para avaliador em edital
   * Lista todas as permissões disponíveis marcando quais estão atribuídas ao avaliador no edital
   * GET /api/admin/permissoes/avaliadores/{avaliadorId}/editais/{editalId}
   */
  listarPermissoesUsuario(avaliadorId: number, editalId: number): Observable<PermissaoDTO[]> {
    return this.http.get<PermissaoDTO[]>(`${this.apiUrl}/avaliadores/${avaliadorId}/editais/${editalId}`);
  }

  /**
   * Atribui permissão a usuário em um edital
   * POST /api/admin/permissoes/atribuir
   */
  atribuirPermissao(request: AtribuirPermissaoRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/atribuir`, request);
  }

  /**
   * Remove permissão de usuário em um edital
   * DELETE /api/admin/permissoes/remover
   * O backend espera um body JSON com os dados da permissão
   */
  removerPermissao(request: AtribuirPermissaoRequest): Observable<void> {
    // Usando HttpRequest para enviar body em DELETE
    const httpRequest = new HttpRequest<AtribuirPermissaoRequest>(
      'DELETE',
      `${this.apiUrl}/remover`,
      request,
      {
        reportProgress: false
      }
    );
    return this.http.request<void>(httpRequest).pipe(
      // Filtrar apenas eventos de resposta HTTP
      filter((event): event is HttpResponse<void> => event instanceof HttpResponse),
      // Mapear para void
      map(() => undefined as void)
    );
  }

  /**
   * Remover todas as permissões de um avaliador para um edital
   * DELETE /api/admin/permissoes/remover-todos/{avaliadorId}/{editalId}
   */
  removerPermissoesUsuario(request: AtribuirPermissaoRequest): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/remover-todos`, {
      body: request,
    });
  }

  /**
   * Lista usuários com uma permissão específica em um edital
   * GET /api/admin/permissoes/editais/{editalId}/permissoes/{codigoPermissao}/usuarios
   */
  listarUsuariosComPermissao(editalId: number, codigoPermissao: string): Observable<UsuarioPermissaoDTO[]> {
    return this.http.get<UsuarioPermissaoDTO[]>(
      `${this.apiUrl}/editais/${editalId}/permissoes/${codigoPermissao}/usuarios`
    );
  }

  /**
   * Lista permissões por categoria
   * GET /api/admin/permissoes/catalogo/categoria/{categoria}
   */
  listarPorCategoria(categoria: string): Observable<PermissaoDTO[]> {
    return this.http.get<PermissaoDTO[]>(`${this.apiUrl}/catalogo/categoria/${categoria}`);
  }

  /**
   * Verifica se o usuário atual tem uma permissão específica em um edital
   * Usa cache para evitar múltiplas requisições
   * @param editalId ID do edital
   * @param codigoPermissao Código da permissão (ex: 'ISENCAO_DEFERIR', 'RECURSO_RESPONDER')
   * @returns Observable<boolean> true se tiver permissão, false caso contrário
   */
  temPermissao(editalId: number, codigoPermissao: string): Observable<boolean> {
    // 1. Admin sempre tem permissão (bypass rápido)
    if (this.authService.isAdmin()) {
      return of(true);
    }

    const currentUser = this.authService.getCurrentUser();

    // 2. Cenário Ideal: Temos usuário E temos o ID
    if (currentUser && currentUser.id) {
      return this._verificarPermissaoLogica(currentUser.id, editalId, codigoPermissao);
    }

    // 3. Cenário do Problema: Temos usuário (logado), mas o ID ainda é undefined
    // Isso acontece logo após o login ou refresh, antes do /me retornar
    if (currentUser && !currentUser.id) {
      // Vamos buscar o perfil forçadamente e DEPOIS verificar a permissão
      return this.authService.fetchUserProfile().pipe(
        switchMap((profile: any) => {
          if (profile && profile.id) {
            // Agora temos o ID, podemos verificar
            return this._verificarPermissaoLogica(profile.id, editalId, codigoPermissao);
          }
          return of(false);
        }),
        catchError(err => {
          console.error('Erro ao recuperar ID do usuário para permissão', err);
          return of(false);
        })
      );
    }

    // 4. Não logado
    return of(false);
  }

  /**
   * Método privado auxiliar para centralizar a lógica de Cache + HTTP
   * Isso evita duplicar código dentro do pipe acima.
   */
  private _verificarPermissaoLogica(userId: number, editalId: number, codigoPermissao: string): Observable<boolean> {
    const cacheKey = `${userId}_${editalId}`;
    const cached = this.permissoesCache.get(cacheKey);
    const cacheTime = this.cacheTimestamps.get(cacheKey);

    // Verificar se cache ainda é válido
    if (cached && cacheTime && (Date.now() - cacheTime) < this.CACHE_DURATION) {
      const temPermissao = cached.some(p => p.codigo === codigoPermissao && p.atribuida && p.ativo);
      return of(temPermissao);
    }

    // Buscar permissões na API
    return this.avaliadorService.getPermissoesAvaliadorEdital(userId, editalId).pipe(
      map(permissoes => {
        // Atualizar cache
        this.permissoesCache.set(cacheKey, permissoes);
        this.cacheTimestamps.set(cacheKey, Date.now());

        // Verificar se tem a permissão específica
        return permissoes.some(p => p.codigo === codigoPermissao && p.atribuida && p.ativo);
      }),
      catchError(error => {
        console.error('Erro ao verificar permissão:', error);
        return of(false);
      })
    );
  }

  /**
   * Verifica se o usuário atual tem permissão (síncrono usando cache)
   * Retorna false se não houver cache
   * @param editalId ID do edital
   * @param codigoPermissao Código da permissão
   * @returns boolean | null (null se não houver cache)
   */
  temPermissaoCache(editalId: number, codigoPermissao: string): boolean | null {
    if (this.authService.isAdmin()) {
      return true;
    }

    const currentUser = this.authService.getCurrentUser();
    // Aqui não tem jeito, se não tiver ID, retorna null (cache miss)
    if (!currentUser?.id) {
      return null;
    }

    const cacheKey = `${currentUser.id}_${editalId}`;
    const cached = this.permissoesCache.get(cacheKey);
    const cacheTime = this.cacheTimestamps.get(cacheKey);

    if (cached && cacheTime && (Date.now() - cacheTime) < this.CACHE_DURATION) {
      return cached.some(p => p.codigo === codigoPermissao && p.atribuida && p.ativo);
    }

    return null;
  }

  limparCache(editalId?: number): void {
    if (editalId) {
      const currentUser = this.authService.getCurrentUser();
      if (currentUser?.id) {
        const cacheKey = `${currentUser.id}_${editalId}`;
        this.permissoesCache.delete(cacheKey);
        this.cacheTimestamps.delete(cacheKey);
      }
    } else {
      this.permissoesCache.clear();
      this.cacheTimestamps.clear();
    }
  }

  /**
   * Lista permissões globais de um usuário
   * GET /api/admin/permissoes/globais/usuario/{usuarioId}
   */
  listarPermissoesGlobaisUsuario(avaliadorId: number): Observable<PermissaoDTO[]> {
    return this.http.get<PermissaoDTO[]>(`${this.apiUrl}/avaliadores/${avaliadorId}`);
  }

  /**
   * Atribui permissão global a um usuário
   * POST /api/admin/permissoes/atribuir-global
   */
  atribuirPermissaoGlobal(request: { usuarioId?: number, avaliadorId?: number, codigoPermissao: string }): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/atribuir-global`, request);
  }

  /**
   * Remove permissão global de um usuário
   * DELETE /api/admin/permissoes/remover-global
   * O backend espera um body JSON com usuarioId ou avaliadorId e codigoPermissao
   */
  removerPermissaoGlobal(request: { usuarioId?: number, avaliadorId?: number, codigoPermissao: string }): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/remover-global`, {
      body: {
        ...request
      }
    });
  }

  /**
   * Verifica se o usuário atual tem permissão global (sem vínculo com edital)
   * Usa cache no localStorage para melhor performance
   * @param codigoPermissao Código da permissão
   * @returns Observable<boolean>
   */
  /**
   * Verifica se o usuário atual tem permissão global (sem vínculo com edital)
   * Usa cache no localStorage para melhor performance
   * Agora lida com o atraso no carregamento do ID do usuário.
   */
  temPermissaoGlobal(codigoPermissao: string): Observable<boolean> {
    // 1. Admin sempre tem permissão
    if (this.authService.isAdmin()) {
      return of(true);
    }

    const currentUser = this.authService.getCurrentUser();

    // 2. Cenário Ideal: Temos usuário E temos o ID
    if (currentUser && currentUser.id) {
      return this._verificarPermissaoGlobalLogica(currentUser.id, codigoPermissao);
    }

    // 3. Cenário do Problema: Temos usuário (logado), mas o ID ainda é undefined
    if (currentUser && !currentUser.id) {
      // Vamos buscar o perfil forçadamente e DEPOIS verificar a permissão global
      return this.authService.fetchUserProfile().pipe(
        switchMap((profile: any) => {
          if (profile && profile.id) {
            // Agora temos o ID, podemos verificar
            return this._verificarPermissaoGlobalLogica(profile.id, codigoPermissao);
          }
          return of(false);
        }),
        catchError(err => {
          console.error('Erro ao recuperar ID do usuário para permissão global', err);
          return of(false);
        })
      );
    }

    // 4. Não logado
    return of(false);
  }

  /**
   * Método privado auxiliar para centralizar a lógica de Cache + HTTP para Globais
   */
  private _verificarPermissaoGlobalLogica(userId: number, codigoPermissao: string): Observable<boolean> {
    // Verificar cache no localStorage
    const cached = this.getGlobalPermissionsFromCache();
    const cacheTime = this.getGlobalPermissionsTimestampFromCache();

    if (cached && cacheTime && (Date.now() - cacheTime) < this.CACHE_DURATION) {
      const temPermissao = cached.some(p =>
        p.codigo === codigoPermissao &&
        p.atribuida === true &&
        p.ativo !== false &&
        !p.vinculoEdital
      );
      return of(temPermissao);
    }

    // Buscar permissões globais na API
    // Nota: Certifique-se que o método getPermissoesAvaliadorGlobal existe no seu AvaliadorService
    return this.avaliadorService.getPermissoesAvaliadorGlobal(userId).pipe(
      map(permissoes => {
        // Salvar no cache (localStorage)
        this.saveGlobalPermissionsToCache(permissoes);

        // Verificar se tem a permissão específica
        return permissoes.some(p =>
          p.codigo === codigoPermissao &&
          p.atribuida === true &&
          p.ativo !== false &&
          !p.vinculoEdital
        );
      }),
      catchError(error => {
        console.error('Erro ao verificar permissão global:', error);
        return of(false);
      })
    );
  }

  /**
   * Verifica permissão global usando cache (síncrono)
   * Retorna null se não houver cache
   */
  temPermissaoGlobalCache(codigoPermissao: string): boolean | null {
    if (this.authService.isAdmin()) {
      return true;
    }

    // Se não tiver ID aqui, infelizmente retornamos null pois é uma chamada síncrona
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser?.id) {
      return null;
    }

    const cached = this.getGlobalPermissionsFromCache();
    const cacheTime = this.getGlobalPermissionsTimestampFromCache();

    if (cached && cacheTime && (Date.now() - cacheTime) < this.CACHE_DURATION) {
      return cached.some(p =>
        p.codigo === codigoPermissao &&
        p.atribuida === true &&
        p.ativo !== false &&
        !p.vinculoEdital
      );
    }

    return null;
  }

  /**
   * Limpa cache de permissões globais
   */
  limparCacheGlobal(): void {
    try {
      localStorage.removeItem(this.GLOBAL_PERMISSIONS_KEY);
      localStorage.removeItem(this.GLOBAL_PERMISSIONS_TIMESTAMP_KEY);
    } catch (error) {
      console.error('Erro ao limpar cache de permissões globais:', error);
    }
  }

  /**
   * Salva permissões globais no localStorage
   */
  private saveGlobalPermissionsToCache(permissoes: PermissaoDTO[]): void {
    try {
      localStorage.setItem(this.GLOBAL_PERMISSIONS_KEY, JSON.stringify(permissoes));
      localStorage.setItem(this.GLOBAL_PERMISSIONS_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Erro ao salvar cache de permissões globais:', error);
    }
  }

  /**
   * Recupera permissões globais do localStorage
   */
  private getGlobalPermissionsFromCache(): PermissaoDTO[] | null {
    try {
      const cached = localStorage.getItem(this.GLOBAL_PERMISSIONS_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Erro ao recuperar cache de permissões globais:', error);
      return null;
    }
  }

  /**
   * Recupera timestamp do cache de permissões globais
   */
  private getGlobalPermissionsTimestampFromCache(): number | null {
    try {
      const timestamp = localStorage.getItem(this.GLOBAL_PERMISSIONS_TIMESTAMP_KEY);
      return timestamp ? parseInt(timestamp, 10) : null;
    } catch (error) {
      console.error('Erro ao recuperar timestamp do cache:', error);
      return null;
    }
  }
}
